// Interactive between-session exercises and the deliverables they feed.

// The deliverables library (shared by participant Deliverables page and coach view).
export const DELIVERABLE_DOCS = [
  { key: "deliverable:positioning", title: "Positioning statement", ai: "sharpen", hint: "Who you help, the problem you solve, and your distinctive edge." },
  { key: "deliverable:transferable-skills", title: "Transferable skills map", ai: "summarize", hint: "Your skills translated from your current audience to your desired one." },
  { key: "deliverable:narrative", title: "Professional narrative", ai: "summarize", hint: "The thread that connects your work (≈200 words)." },
  { key: "deliverable:credibility", title: "Credibility bank", ai: "proof", hint: "10–15 proof points: projects, outcomes, recognition, partnerships." },
  { key: "deliverable:ideal-client", title: "Ideal client profile", ai: "summarize", hint: "Who is most likely to hire you first – trust, relevance, access." },
  { key: "deliverable:problem", title: "Problem statement", ai: "sharpen", hint: "The urgent client problem your experience solves." },
  { key: "deliverable:offer-brief", title: "One-page offer brief", ai: "offer", hint: "Outcome, format, scope, and who it is for." },
  { key: "deliverable:pilot", title: "Pilot / proposal scope", ai: "offer", hint: "Smallest meaningful engagement that proves value." },
  { key: "deliverable:packages", title: "Service packages & pricing", ai: "offer", hint: "2–3 packages with different depth and pricing logic." },
  { key: "deliverable:service-copy", title: "Services page copy", ai: "sharpen", hint: "Client-facing language with rigor and values." },
  { key: "deliverable:client-fit", title: "Client fit filter", ai: "summarize", hint: "Who is aligned, draining, extractive, or off-mission." },
  { key: "deliverable:visibility", title: "Visibility & relationship plan", ai: "plan", hint: "A sustainable rhythm of outreach, thought leadership, referrals." },
  { key: "deliverable:bio", title: "Consultant bio", ai: "bio", hint: "Credible, values-aligned, third-person." },
  { key: "deliverable:roadmap", title: "90-day roadmap", ai: "plan", hint: "Next 30 / 60 / 90 days of practice-building." },
  { key: "deliverable:charter", title: "Practice charter", ai: "summarize", hint: "The purpose of this practice and how you'll protect it." },
  { key: "deliverable:accountability", title: "Accountability structure", ai: "plan", hint: "Post-program checkpoints and measures of progress." },
];

export const ACTIVITY_TOOLS = {
  "transferable-skills": {
    sessionId: 1, title: "Transferable skills map", kind: "custom",
    intro: "Describe your skills in your own words. The assistant will ask a few clarifying questions, then help you translate those skills from your current audience to the one you want to serve.",
    fields: [
      { id: "current_audience", label: "Your current industry / audience", type: "text", placeholder: "Who you've served so far" },
      { id: "desired_audience", label: "Your desired audience", type: "text", placeholder: "Who you want to serve next" },
      { id: "skills_own_words", label: "Your skills, in your own words", type: "textarea" },
      { id: "answers", label: "Your answers to the assistant's questions", type: "textarea" },
    ],
    deliverable: "deliverable:transferable-skills",
  },
  "career-timeline": {
    sessionId: 1, title: "Career timeline", itemLabel: "Pivotal moment", kind: "list", min: 8, max: 10,
    intro: "Map 8–10 pivotal moments. We're looking for the pattern underneath the roles – where you were already acting like an advisor.",
    fields: [
      { id: "when", label: "When", type: "text", placeholder: "Year or period" },
      { id: "moment", label: "What happened", type: "textarea" },
      { id: "why", label: "Why it mattered / what it revealed", type: "textarea" },
    ],
    ai: "summarize", aiLabel: "Find the patterns",
  },
  "energy-audit": {
    sessionId: 1, title: "2-week energy audit", kind: "writing",
    intro: "Track where your energy goes. Notice what is energising, what is draining, and what feels meaningful even when hard.",
    fields: [
      { id: "energizing", label: "Energising work", rows: 4, prompts: ["What kinds of work leave you more alive?"] },
      { id: "draining", label: "Draining work", rows: 4, prompts: ["What consistently depletes you?"] },
      { id: "meaningful", label: "Meaningful work", rows: 4, prompts: ["What feels meaningful even when it's hard?"] },
    ],
    ai: "summarize", aiLabel: "Summarise my energy",
  },
  "peer-feedback": {
    sessionId: 1, title: "Peer feedback scan", itemLabel: "Feedback reflection", kind: "list", min: 5, max: 8,
    intro: "Gather reflections from 5 trusted peers or former colleagues. Capture their words, not your edit of them.",
    fields: [
      { id: "person", label: "Who", type: "text", placeholder: "Name / relationship" },
      { id: "strengths", label: "My distinctive strengths (their words)", type: "textarea" },
      { id: "situations", label: "Situations I handle especially well", type: "textarea" },
    ],
    ai: "summarize", aiLabel: "Synthesise the feedback",
  },
  "consulting-possible": {
    sessionId: 1, title: "What I want consulting to make possible", kind: "writing",
    intro: "A one-page reflection. Write freely, then let the assistant reflect the essence back to you.",
    fields: [{ id: "reflection", label: "Reflection", rows: 10 }],
    ai: "summarize", aiLabel: "Reflect the essence",
  },
  "credibility-bank": {
    sessionId: 2, title: "Credibility bank", itemLabel: "Proof point", kind: "list", min: 10, max: 15,
    intro: "List 10–15 proof points. Then convert them into client-facing language with the assistant and send to your deliverables.",
    fields: [
      { id: "what", label: "Project / role / win", type: "textarea" },
      { id: "outcome", label: "Outcome or recognition", type: "textarea" },
    ],
    ai: "proof", aiLabel: "Turn into proof points",
    deliverable: "deliverable:credibility",
  },
  "professional-narrative": {
    sessionId: 2, title: "Professional narrative", kind: "writing",
    intro: "≈200 words: the thread that connects your work. Draft it, sharpen it, then send to your deliverables.",
    fields: [{ id: "narrative", label: "The thread", rows: 9, prompts: ["What pattern connects your roles and titles?"] }],
    ai: "summarize", aiLabel: "Sharpen the thread",
    deliverable: "deliverable:narrative",
  },
  "positioning-test": {
    sessionId: 2, title: "Positioning test", itemLabel: "Statement test", kind: "list", min: 3, max: 5,
    intro: "Test 3 positioning statements with trusted contacts. Note which language felt true, forced, clear, or vague.",
    fields: [
      { id: "statement", label: "Positioning statement", type: "textarea" },
      { id: "who", label: "Tested with", type: "text" },
      { id: "reaction", label: "Their reaction / what landed", type: "textarea" },
      { id: "verdict", label: "True · forced · clear · vague?", type: "text" },
    ],
    ai: "sharpen", aiLabel: "Sharpen the strongest",
    deliverable: "deliverable:positioning",
  },
  "client-segments": {
    sessionId: 3, title: "Client segments", kind: "writing",
    intro: "Choose your primary and secondary client segments – where you have trust, relevance, and access.",
    fields: [
      { id: "primary", label: "Primary segment", rows: 4, prompts: ["Who is most likely to say yes first?"] },
      { id: "secondary", label: "Secondary segment", rows: 4 },
    ],
    ai: "summarize", aiLabel: "Profile the ideal client",
    deliverable: "deliverable:ideal-client",
  },
  "offer-briefs": {
    sessionId: 3, title: "Offer briefs", itemLabel: "Offer", kind: "list", min: 1, max: 2,
    intro: "Draft 1–2 one-page offer briefs. Refine into clear client-facing language and send to your deliverables.",
    fields: [
      { id: "name", label: "Offer name", type: "text" },
      { id: "who", label: "Who it's for", type: "text" },
      { id: "problem", label: "Problem it solves", type: "textarea" },
      { id: "outcome", label: "Outcome", type: "textarea" },
      { id: "format", label: "Format & scope", type: "textarea" },
    ],
    ai: "offer", aiLabel: "Polish the offer",
    deliverable: "deliverable:offer-brief",
  },
  "intro-script": {
    sessionId: 3, title: "Introduction script", kind: "writing",
    intro: "A short, human \u201cI help…\u201d introduction you can actually say out loud.",
    fields: [{ id: "script", label: "I help…", rows: 4 }],
    ai: "sharpen", aiLabel: "Sharpen the intro",
  },
  "conversation-list": {
    sessionId: 3, title: "Market conversation list", itemLabel: "Contact", kind: "list", min: 10, max: 15,
    intro: "Build a list of 10 potential market conversations, with a short outreach note for each.",
    fields: [
      { id: "name", label: "Name", type: "text" },
      { id: "org", label: "Organisation / context", type: "text" },
      { id: "relationship", label: "Warm · loose · cold", type: "text" },
      { id: "note", label: "Outreach note", type: "textarea" },
    ],
    ai: "draft-outreach", aiLabel: "Draft outreach",
  },
  "market-conversations": {
    sessionId: 4, title: "Market conversations & resonance", itemLabel: "Conversation", kind: "list", min: 3, max: 6,
    intro: "Log 3–5 real conversations. Track resonance and objections, then let the assistant summarise what buyers care about.",
    fields: [
      { id: "contact", label: "Who", type: "text" },
      { id: "landed", label: "What landed", type: "textarea" },
      { id: "didnt", label: "What didn't / confused", type: "textarea" },
      { id: "objection", label: "Objection (relevance, scope, budget, fit)", type: "textarea" },
      { id: "surprised", label: "What surprised me", type: "textarea" },
    ],
    ai: "analyze-conversations", aiLabel: "Summarise resonance",
  },
  "pilot-proposal": {
    sessionId: 4, title: "Pilot / proposal", kind: "writing",
    intro: "Shape the smallest meaningful engagement that proves value – clear outcomes, timeline, and deliverables.",
    fields: [
      { id: "notes", label: "Pilot notes", rows: 6, prompts: ["What is the smallest paid engagement that feels real and manageable?"] },
      { id: "pricing", label: "Pricing hypothesis", rows: 2 },
    ],
    ai: "offer", aiLabel: "Draft the pilot scope",
    deliverable: "deliverable:pilot",
  },
  "service-packages": {
    sessionId: 5, title: "Service packages & pricing", itemLabel: "Package", kind: "list", min: 1, max: 3,
    intro: "Finalise 2–3 service packages with different levels of depth and a pricing range.",
    fields: [
      { id: "name", label: "Package name", type: "text" },
      { id: "depth", label: "Depth / who it's for", type: "textarea" },
      { id: "includes", label: "What's included", type: "textarea" },
      { id: "price", label: "Price range", type: "text" },
    ],
    ai: "offer", aiLabel: "Refine the packages",
    deliverable: "deliverable:packages",
  },
  "services-page": {
    sessionId: 5, title: "Services page copy", kind: "writing",
    intro: "Draft a short services page or capability statement – rigor and values without vagueness.",
    fields: [{ id: "copy", label: "Service copy", rows: 9 }],
    ai: "sharpen", aiLabel: "Sharpen the copy",
    deliverable: "deliverable:service-copy",
  },
  "client-fit": {
    sessionId: 5, title: "Client fit filter", itemLabel: "Criterion", kind: "list", min: 3, max: 8,
    intro: "Define what makes a client aligned – and what slowly erodes your values if you say yes too often.",
    fields: [
      { id: "criterion", label: "Signal / criterion", type: "textarea" },
      { id: "verdict", label: "Aligned · caution · avoid", type: "text" },
    ],
    ai: "summarize", aiLabel: "Summarise the filter",
    deliverable: "deliverable:client-fit",
  },
  "visibility-plan": {
    sessionId: 5, title: "Visibility & relationship plan", kind: "writing",
    intro: "A 12-week rhythm: relationship-building, thought leadership, partnerships, speaking, referrals.",
    fields: [{ id: "plan", label: "Plan notes", rows: 7 }],
    ai: "plan", aiLabel: "Build the weekly rhythm",
    deliverable: "deliverable:visibility",
  },
  "outreach-send": {
    sessionId: 6, title: "First outreach", itemLabel: "Message", kind: "list", min: 5, max: 8,
    intro: "Send your first 5 targeted outreach or follow-up messages. Draft, refine, and track them here.",
    fields: [
      { id: "to", label: "To", type: "text" },
      { id: "message", label: "Message", type: "textarea" },
      { id: "status", label: "Status", type: "text" },
    ],
    ai: "draft-outreach", aiLabel: "Draft messages",
  },
  "accountability": {
    sessionId: 6, title: "Accountability structure", itemLabel: "Checkpoint", kind: "list", min: 2, max: 6,
    intro: "Schedule post-program checkpoints and the measures that tell you you're on track.",
    fields: [
      { id: "when", label: "When", type: "text" },
      { id: "focus", label: "Focus / measure", type: "textarea" },
    ],
    ai: "plan", aiLabel: "Shape the plan",
    deliverable: "deliverable:accountability",
  },
};

// Map (sessionId : activity index) -> tool id.
export const ACTIVITY_LINKS = {
  "1:0": "transferable-skills",
  "1:1": "career-timeline",
  "1:2": "energy-audit",
  "1:3": "peer-feedback",
  "1:4": "consulting-possible",
  "2:0": "credibility-bank",
  "2:1": "professional-narrative",
  "2:2": "positioning-test",
  "3:0": "client-segments",
  "3:1": "offer-briefs",
  "3:2": "intro-script",
  "3:3": "conversation-list",
  "4:0": "market-conversations",
  "4:3": "pilot-proposal",
  "5:0": "service-packages",
  "5:1": "services-page",
  "5:2": "client-fit",
  "5:3": "visibility-plan",
  "6:0": "outreach-send",
  "6:2": "accountability",
};

export function getToolForActivity(sessionId, index) {
  const id = ACTIVITY_LINKS[`${sessionId}:${index}`];
  return id ? { id, ...ACTIVITY_TOOLS[id] } : null;
}

export function getTool(id) {
  return ACTIVITY_TOOLS[id] ? { id, ...ACTIVITY_TOOLS[id] } : null;
}

export function toolHasContent(getEntry, toolId) {
  const tool = ACTIVITY_TOOLS[toolId];
  if (!tool) return false;
  if (tool.kind === "list") {
    const items = getEntry(`tool:${toolId}`, []);
    return Array.isArray(items) && items.some((it) =>
      Object.entries(it || {}).some(([k, v]) => k !== "_id" && String(v || "").trim()));
  }
  return tool.fields.some((f) => String(getEntry(`tool:${toolId}:${f.id}`, "")).trim());
}

export function serializeTool(getEntry, toolId) {
  const tool = ACTIVITY_TOOLS[toolId];
  if (!tool) return "";
  if (tool.kind === "list") {
    const items = getEntry(`tool:${toolId}`, []);
    return (items || [])
      .map((it, i) => `${i + 1}. ` + tool.fields.map((f) => `${f.label}: ${it?.[f.id] || ""}`).join(" | "))
      .join("\n");
  }
  return tool.fields.map((f) => `${f.label}:\n${getEntry(`tool:${toolId}:${f.id}`, "")}`).join("\n\n");
}
